<script>
    import { createEventDispatcher, onMount, tick } from "svelte";

    import debounce from "lodash/debounce";

    import { apiMethods } from "../../../Services/index.svelte";

    import { preOpQuestionnaires, user } from "../../../stores";

    import {
        copyObjContent,
        getAmountInCurrency,
        getFormatDate,
        getFormatDateAndTime,
        getText,
        goToURL,
        showError,
    } from "../../../Utils/utilFunctions.svelte";

    import {
        CardLayout,
        Loader,
        DateInput,
        RichTextEditor,
        DecimalInput,
        NotificationModal,
    } from "../../../Components/Common/index.svelte";

    import { SEARCH, duplicateIcon, editIcon } from "../../../Utils/utilConstants.svelte";

    import AccountAvatarEditor from "./AccountAvatarEditor.svelte";

    import {
        cachePreOpQuestionnaire,
        getFilteredPreOpInstructions,
    } from "../../../Utils/utilCache.svelte";
    import { checkEnvIsGroup, isEnvNewOrApp } from "../../../Utils/utilRoute.svelte";

    import { DirectUserRoles, DoctorTitleTypes } from "../../../Utils/utilEnums.svelte";

    export let doctorDetails = {},
        isUpdatingDoctor = false,
        isEditEnabled = false;

    $: preOpQuestionnaireName =
        doctorDetails.preOpQuestionnaire &&
        filteredPreOpQuestionnaires.length &&
        filteredPreOpQuestionnaires.some(
            (p) => p.id == doctorDetails.preOpQuestionnaire,
        )
            ? filteredPreOpQuestionnaires.find(
                  (p) => p.id == doctorDetails.preOpQuestionnaire,
              ).name
            : "None";

     $: orderProductName =
        doctorProfile.orderProductId &&
        orderProducts.length &&
        orderProducts.some(
            (p) => p.id == doctorProfile.orderProductId,
        )
            ? orderProducts.find(
                  (p) => p.id == doctorProfile.orderProductId,
              ).name
            : "None";

    $: preOpInstructionName =
        doctorDetails.preOpInstructions &&
        filteredPreOpInstructions.length &&
        filteredPreOpInstructions.some(
            (i) => i.id == doctorDetails.preOpInstructions,
        )
            ? filteredPreOpInstructions.find(
                  (i) => i.id == doctorDetails.preOpInstructions,
              ).name
            : "None";

    $: showHearAboutUs = isEnvNewOrApp() && doctorProfile.hearAboutUs;

    $: isUserAdmin = $user.role == DirectUserRoles.Admin;

    $: isGroupEnv = checkEnvIsGroup($user.envSettings);

    let doctorProfile = {};

    let filteredPreOpQuestionnaires = [],
        isLoadingPreOpQuestionnaires = false;
    
    let orderProducts = [],
        isLoadingOrderProducts = false;

    let activeCompanyDocuments = [],
        isLoadingCompanyDocuments = false;

    let selectedOrderProductId = Number(0);
    let needToResetOrderProductAgreement = false;
    let oldOrderProductId = null;
    let isConfirmOrderProductChangeModalOpen = false;

    let filteredPreOpInstructions = [],
        isLoadingPreOpInstructions = false;

    const btnOptions = [
        {
            iconName: "editIcon",
            iFont: editIcon,
            iconFunction: () => _triggerEditDoctorDetails(),
            hasTooltip: false,
            tooltip: "Ask patient to pay total fee",
            btnText: "Edit",
        },
    ];

    const bottomLinks = [
        {
            displayName: "View Doctor Encounters",
            clickFunc: () => _redirectToEncounterTable(),
        },
    ];

    onMount(() => {
        doctorProfile = doctorDetails.profile;
        selectedOrderProductId = doctorProfile.orderProductId;
        oldOrderProductId = doctorProfile.orderProductId;

        loadPreOpQuestionnaires();

        loadPreOpInstructions();

        loadOrderProducts();
        loadCompanyDocuments();
    });

    const loadOrderProducts = async() => {
        if(isGroupEnv || !isUserAdmin) return;

        isLoadingOrderProducts = true;
        
        await apiMethods.getMethod.getOrderProducts(
            0,
            999999,
            {},
        ).then((res) => {
            if (!res)
                showError(
                    "Failed to load order products, please refresh the page and try again and contact support if the issue persists.",
                );
            else if (res.error) showError(res.error);
            else {
                orderProducts = [...res.results];
            }
        }),

        isLoadingOrderProducts = false;
    }

    const loadCompanyDocuments = async () => {
        if (isGroupEnv || !isUserAdmin) return;

        isLoadingCompanyDocuments = true;

        await apiMethods.getMethod
            .getAgreementDocuments(0, 999999, { "filter[active]": 1 })
            .then((res) => {
                if (!res)
                    showError(
                        "Failed to load company documents, please refresh the page and try again and contact support if the issue persists.",
                    );
                else if (res.error) showError(res.error);
                else activeCompanyDocuments = [...res.results];
            })
            .catch((err) => showError(err));

        isLoadingCompanyDocuments = false;
    };

    const handleOrderProductSelectChange = (event) => {
        const newOrderProductId = Number(event.target.value);

        if (newOrderProductId === oldOrderProductId) return;

        isConfirmOrderProductChangeModalOpen = true;
    };

    const confirmOrderProductChange = () => {
    
        if(doctorProfile.orderProductAgreementDate){
            needToResetOrderProductAgreement = true;
            doctorProfile.orderProductAgreementDate = null;
        }
        oldOrderProductId = selectedOrderProductId;
        doctorProfile.orderProductId = selectedOrderProductId;

        isConfirmOrderProductChangeModalOpen = false;
    };

    const cancelOrderProductChange = () => {
        selectedOrderProductId = oldOrderProductId;
        isConfirmOrderProductChangeModalOpen = false;

    };

    const loadPreOpQuestionnaires = async () => {
        isLoadingPreOpQuestionnaires = true;

        await cachePreOpQuestionnaire();

        // wait store reactive update finish
        await tick();

        filteredPreOpQuestionnaires = doctorDetails.groupId
            ? $preOpQuestionnaires.filter(
                  (questionnaire) =>
                      questionnaire.groupId == doctorDetails.groupId ||
                      !questionnaire.groupId,
              )
            : $preOpQuestionnaires.filter(
                  (questionnaire) => !questionnaire.groupId,
              );

        isLoadingPreOpQuestionnaires = false;
    };

    const loadPreOpInstructions = async () => {
        isLoadingPreOpInstructions = true;

        filteredPreOpInstructions =
            await getFilteredPreOpInstructions(doctorDetails);

        isLoadingPreOpInstructions = false;
    };

    const dispatch = createEventDispatcher();

    const updatePublishToPortal = debounce(() => {
        if (isEditEnabled) return;
        // dispatch("saveDoctor", doctorProfile);
        apiMethods.putMethod
            .updateDoctor({ ...doctorDetails })
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to update doctor details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    doctorDetails = { ...res };
                    doctorProfile = doctorDetails.profile;
                }
            })
            .catch((err) => showError(err));
    }, SEARCH.DEBOUNCE_TIME);

    // HELPER FUNCTIONS
    const _triggerEditDoctorDetails = () => (isEditEnabled = true);

    const viewCompanyDocument = (documentId) => {
        if (!documentId) return;

        goToURL(`/CompanyDocuments/&id=${documentId}`);
    };

    const viewOrderProductAgreement = () => {
        if (!doctorProfile?.orderProductId) return;

        goToURL(`/OrderProducts/&id=${doctorProfile.orderProductId}`);
    };
 
    $: activeTermsDocument = activeCompanyDocuments.find(
        (doc) => (doc.documentType ?? doc.type)?.toString() === "0",
    );

    $: activePrivacyDocument = activeCompanyDocuments.find(
        (doc) => (doc.documentType ?? doc.type)?.toString() === "1",
    );

    $: isInvalidPhone =
        doctorDetails.phone &&
        !String(doctorDetails.phone)
            .replaceAll(" ", "")
            .toLocaleLowerCase()
            .match(/^\(?(\d{2})\)?[\s-]?\d{4}[\s-]?\d{4}$/);

    const _redirectToEncounterTable = () => {
        goToURL(
            `/PatientEncounter/page=0&size=25&doctorId=${doctorDetails.id}`,
        );
    };

    const formSubmit = async () => {
        if(needToResetOrderProductAgreement){
            //reset order product agreement
            await apiMethods.postMethod.resetOrderProductAgreementDate(doctorDetails.id);
        }

        dispatch("saveDoctor", doctorProfile);
    }
</script>

<CardLayout title="Doctor Details" {btnOptions} {bottomLinks}>
    <form
        class="flex flex-col flex-1 px-2 py-4 w-full"
        on:submit|preventDefault={formSubmit}
    >
        <div class="flex flex-col flex-1 w-full px-2 lg:flex-row">
            <div class="flex flex-col flex-1 gap-y-4 h-full">
                <div class="flex flex-row gap-x-2">
                    <p class="title">Full Name:</p>
                    <p class="content">
                        {#if isEditEnabled}
                            <select
                                class="select select-sm select-bordered flex-1"
                                bind:value={doctorDetails.title}
                            >
                                {#each DoctorTitleTypes as title}
                                    <option value={title.id}>{title.type}</option>
                                {/each}
                            </select>
                            {getText(doctorDetails.fullName)}
                        {:else}
                           {doctorDetails.title != 20 ? `${getText(doctorDetails.titleName)}. ` : ''}{getText(doctorDetails.fullName)}
                        {/if}
                    </p>
                </div>
                {#if showHearAboutUs}
                    <div class="flex flex-row gap-x-2 pb-2 w-full">
                        <p class="title">Heard about Us:</p>

                        <p class="content">
                            {getText(doctorProfile.hearAboutUs)}
                        </p>
                    </div>
                {/if}
            </div>

            <div class="divider lg:divider-horizontal" />

            <div class="flex flex-col flex-1 gap-y-4 h-full">
                <div class="flex flex-row gap-x-2">
                    <p class="title">Registered ABN:</p>

                    {#if isEditEnabled}
                        <input
                            type="text"
                            id="abn"
                            class="input max-w-xs input-xs"
                            autocomplete="off"
                            placeholder="ABN"
                            bind:value={doctorProfile.abn}
                        />
                    {:else}
                        <p class="content">{getText(doctorProfile.abn)}</p>
                    {/if}
                </div>
            </div>

            <div class="divider lg:divider-horizontal" />

            <div class="flex flex-col flex-1 gap-y-4 h-full justify-between">
                <div class="flex flex-col gap-y-4">
                    <div class="flex flex-row gap-x-2">
                        <p class="title">Created Date:</p>

                        <p class="content">
                            {getFormatDate(doctorDetails.createdDate)}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex flex-col flex-1 w-full px-2 lg:flex-row">
            <div
                class="flex flex-col flex-1 gap-y-4 h-full justify-start {showHearAboutUs
                    ? 'pt-2'
                    : 'pt-4'}"
            >
                <div class="flex flex-row gap-x-2">
                    <p class="title">Phone Number:</p>
                    {#if isEditEnabled}
                        <input
                            type="text"
                            id="doctorPhoneField"
                            class="input max-w-xs input-xs {isInvalidPhone
                                ? 'border-error'
                                : ''}"
                            autocomplete="off"
                            placeholder="Phone"
                            bind:value={doctorDetails.phone}
                            on:blur={() =>
                                (doctorDetails.phone =
                                    doctorDetails.phone.replaceAll(" ", ""))}
                            on:focus={({ target }) => {
                                target.style.outline = "";
                                target.placeholder = "Phone";
                            }}
                        />
                    {:else}
                        <p class="content">{getText(doctorDetails.phone)}</p>
                    {/if}
                </div>
                {#if isInvalidPhone}
                    <!-- svelte-ignore a11y-label-has-associated-control -->
                    <label class="label">
                        <span class="label-text-alt text-error">
                            Invalid phone number
                        </span>
                    </label>
                {/if}

                <div class="flex flex-row gap-x-2">
                    <p class="title">Date of Birth:</p>
                    {#if isEditEnabled}
                        <DateInput
                            bind:value={doctorDetails.birthDate}
                            placeholder="Birth Date"
                            inputClass="px-[6px] py-[1px]"
                            wrapperClass="input input-sm px-0 input-bordered bg-[#e5e7eb] text-[15px] w-[198px] max-w-[198px] h-[24px] rounded-[2px]"
                            maxDate={new Date()}
                        />
                    {:else}
                        <p class="content">
                            {getFormatDate(doctorDetails.birthDate)}
                        </p>
                    {/if}
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">AHPRA Number:</p>
                    {#if isEditEnabled}
                        <input
                            type="text"
                            id="ahpra"
                            class="input max-w-xs input-xs"
                            autocomplete="off"
                            placeholder="AHPRA Number"
                            bind:value={doctorProfile.ahpra}
                        />
                    {:else}
                        <p class="content">{getText(doctorProfile.ahpra)}</p>
                    {/if}
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Qualifications:</p>

                    {#if isEditEnabled}
                        <input
                            type="text"
                            id="qualifications"
                            class="input max-w-xs input-xs"
                            autocomplete="off"
                            placeholder="Qualifications"
                            bind:value={doctorProfile.qualifications}
                        />
                    {:else}
                        <p class="content">
                            {getText(doctorProfile.qualifications)}
                        </p>
                    {/if}
                </div>

                <div
                    class="flex flex-row flex-1 gap-x-4 justify-between items-end"
                >
                    <div class="flex flex-col gap-y-4">
                        <div class="flex flex-row gap-x-2">
                            <p class="w-[180px]">Hand Over:</p>

                            <input
                                type="checkbox"
                                class="toggle toggle-secondary"
                                bind:checked={doctorDetails.handOver}
                                disabled={!isEditEnabled}
                            />
                        </div>
                         <div class="flex flex-row gap-x-2">
                            <p class="w-[180px]">Registered for GST:</p>

                            <input
                                type="checkbox"
                                class="toggle toggle-secondary"
                                bind:checked={doctorDetails.isRegisteredForGST}
                                disabled={!isEditEnabled}
                            />
                        </div>

                        <div class="flex flex-1 gap-x-2">
                            <p class="w-[180px]">
                                Publish Customisations to Portal:
                            </p>

                            <input
                                type="checkbox"
                                class="toggle toggle-secondary"
                                bind:checked={doctorProfile.publishProfileToPortal}
                                on:change={updatePublishToPortal}
                                disabled={!isEditEnabled}
                            />
                        </div>
                    </div>

                    {#if isUserAdmin}
                        <div class="flex flex-1 gap-x-2">
                            <p class="title">Has a Personalised Portal:</p>

                            <input
                                type="checkbox"
                                class="toggle toggle-secondary"
                                bind:checked={doctorDetails.personalisedPortal}
                                disabled={!isEditEnabled}
                            />
                        </div>
                    {/if}
                </div>

                <div class="flex flex-1 items-start">
                    <p class="title">Profile Picture:</p>

                    <AccountAvatarEditor
                        userAccountId={doctorDetails.id}
                        disabled={!isEditEnabled}
                        accountName={doctorDetails.fullName}
                        avatarType={Number(2)}
                    />
                </div>
            </div>

            <div class="divider lg:divider-horizontal" />

            <div
                class="flex flex-col flex-1 gap-y-4 h-full justify-start {showHearAboutUs
                    ? ''
                    : 'pt-4'}"
            >
                <div class="flex flex-row gap-x-2">
                    <p class="title">BSB:</p>

                    {#if isEditEnabled}
                        <input
                            type="text"
                            id="bankAccountBSB"
                            class="input max-w-xs input-xs"
                            autocomplete="off"
                            placeholder="BSB Number"
                            bind:value={doctorProfile.bankAccountBSB}
                        />
                    {:else}
                        <p class="content">
                            {getText(doctorProfile.bankAccountBSB)}
                        </p>
                    {/if}
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Account No.:</p>

                    {#if isEditEnabled}
                        <input
                            type="text"
                            id="bankAccountNumber"
                            class="input max-w-xs input-xs"
                            autocomplete="off"
                            placeholder="Bank Account Number"
                            bind:value={doctorProfile.bankAccountNumber}
                        />
                    {:else}
                        <p class="content">
                            {getText(doctorProfile.bankAccountNumber)}
                        </p>
                    {/if}
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Account Name:</p>

                    {#if isEditEnabled}
                        <input
                            type="text"
                            id="bankAccountName"
                            class="input max-w-xs input-xs"
                            autocomplete="off"
                            placeholder="Account Name"
                            bind:value={doctorProfile.bankAccountName}
                        />
                    {:else}
                        <p class="content">
                            {getText(doctorProfile.bankAccountName)}
                        </p>
                    {/if}
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Underpayment Threshold</p>

                    {#if isEditEnabled}
                        <div class="flex flex-1">
                            <DecimalInput
                                bind:value={doctorDetails.underPayThreshold}
                                inputClass="w-full"
                            />
                        </div>
                    {:else}
                        <p class="content">
                            {getAmountInCurrency(
                                doctorDetails.underPayThreshold,
                            )}
                        </p>
                    {/if}
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Pre-Op Questionnaire:</p>
                    {#if isLoadingPreOpQuestionnaires}
                        <div class="flex gap-x-2">
                            <Loader />
                            <p>Loading Pre-Op Questionnaire</p>
                        </div>
                    {:else if isEditEnabled}
                        <select
                            class="input input-sm input-bordered min-w-24"
                            bind:value={doctorDetails.preOpQuestionnaire}
                        >
                            <option
                                value={Number(0)}
                                selected={!doctorDetails.preOpQuestionnaire}
                            >
                                None
                            </option>
                            {#each filteredPreOpQuestionnaires as { id, name }}
                                <option
                                    value={id}
                                    selected={doctorDetails.preOpQuestionnaire ==
                                        id}
                                >
                                    {name}
                                </option>
                            {/each}
                        </select>
                    {:else if preOpQuestionnaireName}
                        <p class="content truncate">
                            {getText(preOpQuestionnaireName)}
                        </p>
                    {:else}
                        <p class="content">-</p>
                    {/if}
                </div>

                <div class="flex flex-1 items-start">
                    <p class="title">Patient Facing Picture:</p>
                    <AccountAvatarEditor
                        userAccountId={doctorDetails.id}
                        disabled={!isEditEnabled}
                        accountName={doctorDetails.fullName}
                        avatarType={Number(3)}
                    />
                </div>
            </div>

            <div class="divider lg:divider-horizontal" />

            <div
                class="flex flex-col flex-1 gap-y-4 h-full justify-start {showHearAboutUs
                    ? ''
                    : 'pt-4'}"
            >
                <div class="flex flex-col gap-y-4">
                    <div class="flex flex-row gap-x-2">
                        <p class="title">Last Updated Date:</p>

                        <p class="content">
                            {getFormatDate(doctorDetails.updatedDate)}
                        </p>
                    </div>

                    <div class="flex flex-row gap-x-2">
                        <p class="title">Onboarding Forms Sent:</p>

                        <p
                            class={`content flex items-end ${
                                doctorDetails.sentOnboardingFormsDate
                                    ? ""
                                    : "text-error"
                            }`}
                        >
                            {doctorDetails.sentOnboardingFormsDate
                                ? getFormatDate(
                                      doctorDetails.sentOnboardingFormsDate,
                                  )
                                : "Not Sent"}
                        </p>
                    </div>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Calendar Colour:</p>

                    <input
                        type="color"
                        class="input max-w-xs input-xs"
                        autocomplete="off"
                        disabled={!isEditEnabled}
                        bind:value={doctorDetails.calendarColour}
                    />
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Claim Delay Hours:</p>

                    {#if isEditEnabled}
                        <select
                            class="select-xs w32"
                            bind:value={doctorDetails.claimDelayHours}
                        >
                            {#each Array(73) as _, index}
                                <option value={index}>{index}</option>
                            {/each}
                        </select>
                    {:else}
                        <p>
                            {doctorDetails.claimDelayHours}
                        </p>
                    {/if}
                    Hours
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Default Pre-Op Instruction:</p>
                    {#if isLoadingPreOpInstructions}
                        <div class="flex gap-x-2">
                            <Loader />
                            <p>Loading Pre-Op Instructions</p>
                        </div>
                    {:else if isEditEnabled}
                        <select
                            class="input input-sm input-bordered min-w-24"
                            bind:value={doctorDetails.preOpInstructions}
                        >
                            <option
                                value={Number(0)}
                                selected={!doctorDetails.preOpInstructions}
                            >
                                None
                            </option>
                            {#each filteredPreOpInstructions as { id, name }}
                                <option
                                    value={id}
                                    selected={doctorDetails.preOpInstructions ==
                                        id}>{name}</option
                                >
                            {/each}
                        </select>
                    {:else if preOpInstructionName}
                        <p class="content truncate">
                            {getText(preOpInstructionName)}
                        </p>
                    {:else}
                        <p class="content">-</p>
                    {/if}
                </div>
                <div class="flex flex-row gap-x-2">
                    <p class="title">
                        Schedule email:
                    </p>
                    <div class="content truncate flex flex-row gap-x-1 items-center" style="word-break: break-word; text-wrap: inherit;">
                        <div title="Click to copy"
                            class="copyIcon fa-regular fa-copy text-[16px] hover:cursor-pointer" 
                            on:click={() => copyObjContent(doctorDetails.scheduleEmail, "Schedule email successfully copied to clipboard")}></div>
                        <p title="Click to send email" 
                            class="text-blue-600 underline cursor-pointer hover:text-blue-800 cursor:pointer" 
                            onclick={`window.location.href='mailto:${doctorDetails.scheduleEmail}'`}>{getText(doctorDetails.scheduleEmail)}</p>
                    </div>
                </div>
            </div>
            
        </div>

        {#if !isGroupEnv && isUserAdmin}
            <div class="flex flex-1 w-full flex-row px-2 pt-6 gap-y-4">
                <div class="flex-1 flex flex-col gap-y-2">
                    <div class="flex flex-row gap-x-2 items-center">
                        Terms & Conditions Document Agreement:
                    </div>
                    <div class="flex flex-col gap-y-2">
                        <div class="flex flex-row gap-x-2">
                            <p class="title">Version:</p>
                            {#if isLoadingCompanyDocuments}
                                <p class="content">Loading...</p>
                            {:else if activeTermsDocument}
                                <p class="content">
                                    <button
                                        class="link-button"
                                        on:click={() =>
                                            viewCompanyDocument(activeTermsDocument.id)}
                                    >
                                        {getText(activeTermsDocument.versionNumber)}
                                    </button>
                                </p>
                            {:else}
                                <p class="content">-</p>
                            {/if}
                        </div>
                        {#if doctorProfile.termsAndConditionsAgreementDate && activeTermsDocument?.updatedDate && new Date(doctorProfile.termsAndConditionsAgreementDate) > new Date(activeTermsDocument.updatedDate)}
                            <div class="flex flex-row gap-x-2">
                                <p class="title">Agreed on:</p>
                                <p class="content">
                                {getFormatDateAndTime(doctorProfile.termsAndConditionsAgreementDate)}
                                </p>
                            </div>
                        {:else}
                            <div class="flex flex-row gap-x-2">
                                Collecting from user
                            </div>
                        {/if}
                    </div>
                </div>

                <div class="divider lg:divider-horizontal" />

                <div class="flex-1 flex flex-col  gap-y-2">
                    <div class="flex flex-row gap-x-2 items-center">
                        Privacy Policy Document Agreement:
                    </div>
                    <div class="flex flex-col gap-y-2">
                        <div class="flex flex-row gap-x-2">
                            <p class="title">Version:</p>
                            {#if isLoadingCompanyDocuments}
                                <p class="content">Loading...</p>
                            {:else if activePrivacyDocument}
                                <p class="content">
                                    <button
                                        class="link-button"
                                        on:click={() =>
                                            viewCompanyDocument(activePrivacyDocument.id)}
                                    >
                                        {getText(activePrivacyDocument.versionNumber)}
                                    </button>
                                </p>
                            {:else}
                                <p class="content">-</p>
                            {/if}
                        </div>
                        {#if doctorProfile.privacyPolicyAgreementDate && activePrivacyDocument?.updatedDate && new Date(doctorProfile.privacyPolicyAgreementDate) > new Date(activePrivacyDocument.updatedDate)}
                            <div class="flex flex-row gap-x-2">
                                <p class="title">Agreed on:</p>
                                <p class="content">
                                {getFormatDateAndTime(doctorProfile.privacyPolicyAgreementDate)}
                                </p>
                            </div>
                        {:else}
                            <div class="flex flex-row gap-x-2">
                                Collecting from user
                            </div>
                        {/if}
                    </div>
                </div>

                <div class="divider lg:divider-horizontal" />

                <div class="flex-1 flex flex-col gap-y-2">
                    <div class="flex flex-row gap-x-2 items-center">
                        Order Product Agreement:
                    </div>
                    <div class="flex flex-col gap-y-2">
                        <div class="flex flex-row gap-x-2">
                            <p class="title">Product name:</p>
                            <div class="content">
                                {#if isLoadingOrderProducts}
                                    <div class="flex gap-x-2">
                                        <Loader />
                                        <p>Loading order products</p>
                                    </div>
                                {:else if isEditEnabled}
                                    <select
                                        class="input input-sm input-bordered w-full"
                                        bind:value={selectedOrderProductId}
                                        on:change={handleOrderProductSelectChange}
                                    >
                                        <option value={Number(0)}>None</option>
                                        {#each orderProducts as { id, name }}
                                            <option value={id}>{name}</option>
                                        {/each}
                                    </select>
                                {:else if orderProductName}
                                    <button
                                        class="link-button truncate"
                                        on:click={viewOrderProductAgreement}
                                    >
                                        {getText(orderProductName)}
                                    </button>
                                {:else}
                                    <span>-</span>
                                {/if}
                            </div>
                        </div>
                        {#if doctorProfile.orderProductAgreementDate}
                            <div class="flex flex-row gap-x-2">
                                <p class="title">Agreed on:</p>
                                <p class="content">
                                {getFormatDateAndTime(doctorProfile.orderProductAgreementDate)}
                                </p>
                            </div>
                        {:else}
                            {#if doctorProfile.orderProductId != 0}
                                <div class="flex flex-row gap-x-2">
                                    Collecting from user
                                </div>
                            {/if}
                        {/if}
                    </div>
                </div>
                
            </div>
        {/if}

        <div class="flex flex-1 w-full flex-col pt-6 gap-y-4">
            <div class="flex flex-1 items-start px-2 gap-x-2">
                <p class="title">About Me:</p>
                <div class="w-remaining flex">
                    {#if doctorProfile.aboutMe || isEditEnabled}
                        <RichTextEditor
                            bind:content={doctorProfile.aboutMe}
                            config={{ isDisabled: !isEditEnabled }}
                        />
                    {/if}
                </div>
            </div>

            <div class="flex flex-1 items-start px-2">
                <p class="title flex-0">Pre-Op Text:</p>
                <div class="w-remaining flex">
                    {#if doctorProfile.preOpText || isEditEnabled}
                        <RichTextEditor
                            bind:content={doctorProfile.preOpText}
                            config={{ isDisabled: !isEditEnabled }}
                        />
                    {/if}
                </div>    
            </div>

            <div class="flex flex-1 w-full justify-end">
                {#if isEditEnabled}
                    <div class="flex flex-row w-1/3 justify-end gap-x-4">
                        <button
                            class="btn btn-secondary flex-1 btn-sm"
                            on:click|preventDefault={() =>
                                dispatch("cancelEdit")}
                            disabled={isUpdatingDoctor}
                        >
                            Cancel Edit
                        </button>
                        <button
                            class="btn btn-primary flex-1 btn-sm"
                            disabled={isUpdatingDoctor || isInvalidPhone}
                        >
                            {isUpdatingDoctor ? "Saving" : "Save"}
                        </button>
                    </div>
                {/if}
            </div>
        </div>
       
    </form>

    <NotificationModal
        title="Change Order Product"
        isModalOpen={isConfirmOrderProductChangeModalOpen}
        closeModal={cancelOrderProductChange}
        closeModalFunction={cancelOrderProductChange}
        confirmAction={confirmOrderProductChange}
        submitText="Yes"
        cancelText="No"
        isReverseBtns
    >
        {selectedOrderProductId == 0 ? 'Are you sure you want to remove the order product from this doctor? This will affect the fees collection process' : 'Are you sure you want to change the order product for this doctor? The user will be prompted to agree with the new details.'}
        
    </NotificationModal>
</CardLayout>

<style>
    .title {
        width: 150px;
    }

    .w-remaining {
        width: calc(100% - 150px);
    }

    .title-200 {
        width: 200px;
    }

    .content {
        width: calc(100% - 150px);
    }

    .content-200 {
        width: calc(100% - 200px);
    }

    input[type="text"],
    input[type="color"],
    [type="number"],
    select {
        background-color: #e5e7eb;
        border-radius: 2px;
        font-size: 15px;
        width: 198px;
        max-width: 198px;
    }

    select,
    .w32 {
        width: 128px !important;
    }
    
    .link-button {
        background: none;
        border: none;
        padding: 0;
        margin: 0;
        color: #2563eb;
        text-decoration: underline;
        font: inherit;
        cursor: pointer;
    }

    .link-button:hover {
        color: #1d4ed8;
    }

    textarea {
        background-color: #e5e7eb;
        border-radius: 2px;
        font-size: 15px;
        color: #6f726c;
    }
</style>
